// ScanStatusChip — design §5.2's single, shared progress chip: "the scan and
// build records gain server-written stage fields... a single status chip
// renders those stages plus elapsed time identically in every sub-view."
// Story #5305 Stream 14c (Task #5319) builds this component with the Overlap
// compute run as its FIRST consumer (whitespace/handler.py's real
// OVERLAP_RUN_STAGE_* strings — 'fetching documents' -> 'comparing projects'
// -> 'forming groups' -> 'ready') — see OverlapView.tsx. It takes an
// arbitrary ordered stage list rather than hardcoding one vocabulary so a
// later scan/map-build consumer (design's own 'queued' -> ... -> 'ready'
// ladder) can reuse it unchanged.
//
// Progress is real, never invented (design: "Client polling never gives up
// silently"): the step dots reflect the CALLER's real `stage` value against
// its real `stages` list — an unknown stage renders as a bare label with no
// dots rather than guessing a position.
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { formatElapsedShort } from '../../lib/whitespace'

export interface ScanStatusChipProps {
  /** The server's current stage string, or null before the first stage
   * write lands. */
  stage: string | null
  /** The full ordered stage vocabulary this run type uses (e.g. Overlap's
   * OVERLAP_RUN_STAGES) — used only to place the step dots; an unrecognised
   * `stage` (a future stage this build doesn't know about yet) still
   * renders honestly rather than crashing on a missing index. */
  stages: readonly string[]
  /** ISO timestamp the run started — drives the ticking elapsed clock.
   * Omit (or null) to render the chip without an elapsed reading. */
  startedAt?: string | null
  /** 'computing' (first run / re-run in flight) vs 'updating' — design:
   * "Rescans keep the previous results visible under an 'Updating…' chip
   * instead of blanking every view." Both render the same stage/elapsed
   * machinery; only the lead word and icon differ. */
  variant?: 'computing' | 'updating'
  className?: string
}

/** design's fixed capitalisation convention for a raw stage string
 * ("fetching documents" -> "Fetching documents") — never invented prose,
 * just the server's own words, sentence-cased. */
function capitalize(stage: string): string {
  return stage.length === 0 ? stage : stage[0].toUpperCase() + stage.slice(1)
}

export default function ScanStatusChip({ stage, stages, startedAt, variant = 'computing', className = '' }: ScanStatusChipProps) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!startedAt) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [startedAt])

  const stageIndex = stage ? stages.indexOf(stage) : -1
  const elapsed = formatElapsedShort(startedAt, now)
  const lead = variant === 'updating' ? 'Updating' : 'Working'

  return (
    <span
      data-testid="scan-status-chip"
      className={`inline-flex items-center gap-1.5 rounded-full border border-iflow-200 bg-iflow-50 px-2.5 py-1 text-[11px] font-medium text-iflow-700 ${className}`}
    >
      <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" aria-hidden />
      <span>
        {lead}
        {stage ? `: ${capitalize(stage)}` : '…'}
      </span>
      {stageIndex >= 0 && (
        <span className="flex items-center gap-0.5" aria-hidden data-testid="scan-status-chip-dots">
          {stages.map((s, i) => (
            <span
              key={s}
              className={`w-1.5 h-1.5 rounded-full ${i <= stageIndex ? 'bg-iflow-600' : 'bg-iflow-200'}`}
            />
          ))}
        </span>
      )}
      {elapsed && <span className="text-iflow-500">· {elapsed}</span>}
    </span>
  )
}
